import React, { ComponentType, ReactNode, createElement, useCallback, useRef } from 'react';
import SwiperInstance from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types/swiper-options';

import { Autoplay, Navigation } from 'swiper/modules';

import 'swiper/css';
import { SxProps, styled } from '@cp/ds';
import { Box, BoxProps } from '@cp/ds/src/components/box';

const Root = styled(Box, { name: 'Swiper', slot: 'root' })({
  position: 'relative',
}) as unknown as ComponentType<BoxProps>;

const Wrapper = styled(Box, { name: 'Swiper', slot: 'wrapper' })({
  // display: 'flex',
  // flexDirection: 'row',
  '.swiper-wrapper': {
    display: 'flex',
    flexDirection: 'row',
  },
}) as unknown as ComponentType<BoxProps>;

export interface SliderProps extends BoxProps {
  wrapperSx?: SxProps;
  config?: SwiperOptions;
  onSwiper?: (swiper: SwiperInstance) => void;
  onSlideChange?: (swiper: SwiperInstance) => void;
  after?: ReactNode;
  before?: ReactNode;
  children?: ReactNode;
}

export const SwiperSlider = React.forwardRef<SwiperInstance, SliderProps>(
  ({ config, onSwiper: _onSwiper, onSlideChange, before, after, ...props }, ref) => {
    const children = props.children ? (Array.isArray(props.children) ? props.children : [props.children]) : [];
    const innerSwiperRef = useRef<SwiperInstance>(null);

    const onSwiper = useCallback(
      (swiper: SwiperInstance) => {
        innerSwiperRef.current = swiper;
        _onSwiper?.(swiper);

        if (typeof ref === 'function') {
          ref(swiper);
        } else if (ref) {
          ref.current = swiper;
        }
      },
      [_onSwiper, ref],
    );

    return (
      <Root {...props}>
        {before}
        <Wrapper>
          {createElement(
            Swiper,
            {
              modules: [Navigation, Autoplay],
              ...config,
              onSwiper,
              onSlideChange,
            },
            children.map((slide, key) => createElement(SwiperSlide, { key }, slide)),
          )}
        </Wrapper>
        {after}
      </Root>
    );
  },
);

SwiperSlider.displayName = 'SwiperSlider';
